// ─── Switcher de calendriers (overlay palette) — porté MoPCal ────
// Ouvert par DOUBLE-CLIC sur l'onglet Calendrier. Liste les calendriers
// accessibles (espace + MoPCal perso affichés) avec recherche :
//   ★ / tap ligne = calendrier ACTIF (nouveaux événements) + rendu visible
//   👁            = afficher / masquer (superposition)
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, TextInput, StyleSheet } from 'react-native';
import { useUI, HIT, useT } from '@mop/core';
import { useCalendrierActif } from './CalendrierActifContext';

export default function SwitcherCalendriers({ visible, onClose }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { calendriers, calIdActif, setCalIdActif, estVisible, toggleVisible } = useCalendrierActif();
  const [q, setQ] = useState('');

  const filtre = q.trim().toLowerCase();
  const liste = filtre ? calendriers.filter(c => (c.nom || '').toLowerCase().includes(filtre)) : calendriers;

  // L'actif est toujours affiché → il suffit de le définir comme actif.
  const activer = (id) => setCalIdActif(id);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} onShow={() => setQ('')}>
      <TouchableOpacity style={s.backdrop} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={s.card} activeOpacity={1} onPress={() => {}}>
          <View style={s.entete}>
            <Text style={s.titre}>{t('finances.vueCalendriers')}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={HIT} accessibilityLabel={t('commun.fermer')}><Text style={s.fermer}>✕</Text></TouchableOpacity>
          </View>

          {calendriers.length > 4 && (
            <View style={s.searchRow}>
              <Text style={s.searchIc}>🔎</Text>
              <TextInput style={s.search} placeholder={t('calendrier.rechercherCalendrier')} placeholderTextColor={UI.faint} value={q} onChangeText={setQ} />
            </View>
          )}

          <ScrollView style={{ maxHeight: 380 }} keyboardShouldPersistTaps="handled">
            <Text style={s.section}>{t('calendrier.switcherLegende')}</Text>
            {liste.length === 0 && <Text style={s.vide}>{t('calendrier.switcherVide')}</Text>}
            {liste.map(c => {
              const actif = c.id === calIdActif;
              const vis = estVisible(c.id);
              return (
                <TouchableOpacity key={c.id} style={[s.item, actif && s.itemActif, !vis && s.itemMasque]} activeOpacity={0.7} onPress={() => activer(c.id)}>
                  <TouchableOpacity onPress={() => activer(c.id)} hitSlop={HIT} style={s.etoileBtn} accessibilityLabel={t('calendrier.definirActif')}>
                    <Text style={[s.etoile, { color: actif ? '#F59E0B' : UI.faint }]}>{actif ? '★' : '☆'}</Text>
                  </TouchableOpacity>
                  <View style={[s.chip, { backgroundColor: (c.couleur || UI.accent) }]}><Text style={s.chipTxt}>{c.emoji || '📅'}</Text></View>
                  <View style={{ flex: 1 }}>
                    <Text style={[s.nom, !vis && s.nomMasque]} numberOfLines={1}>{c.nom || t('calendrier.titre')}</Text>
                    <Text style={s.sub} numberOfLines={1}>{c.source === 'mopcal' ? t('calendrier.sourceMopcal') : t('calendrier.sourceEspace')}{actif ? t('calendrier.actifSuffixe') : ''}</Text>
                  </View>
                  <TouchableOpacity onPress={() => toggleVisible(c.id)} disabled={actif} hitSlop={HIT} style={s.oeilBtn}
                    accessibilityLabel={actif ? t('calendrier.toujoursAffiche') : (vis ? t('calendrier.masquer') : t('calendrier.afficher'))}>
                    <Text style={[s.oeil, { opacity: actif ? 0.35 : (vis ? 1 : 0.4) }]}>{vis ? '👁️' : '🙈'}</Text>
                  </TouchableOpacity>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          <Text style={s.pied}>{t('calendrier.switcherPied')}</Text>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}


const makeStyles = (UI) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: UI.overlay, alignItems: 'center', justifyContent: 'center', padding: 22 },
  card: { width: '100%', maxWidth: 380, backgroundColor: UI.card, borderRadius: 16, borderWidth: 0.5, borderColor: UI.border, overflow: 'hidden' },
  entete: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 14, paddingBottom: 10 },
  titre: { flex: 1, fontSize: 17, fontWeight: '800', color: UI.ink },
  fermer: { fontSize: 19, color: UI.accent },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 9, borderTopWidth: 0.5, borderBottomWidth: 0.5, borderColor: UI.border },
  searchIc: { fontSize: 14 },
  search: { flex: 1, fontSize: 14, color: UI.ink, padding: 0 },
  section: { fontSize: 11, color: UI.faint, paddingHorizontal: 14, paddingTop: 10, paddingBottom: 4, letterSpacing: 0.3 },
  vide: { fontSize: 13, color: UI.faint, fontStyle: 'italic', padding: 14 },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, paddingVertical: 9, marginHorizontal: 6, borderRadius: 10 },
  itemActif: { backgroundColor: UI.accentTint },
  itemMasque: { opacity: 0.6 },
  etoileBtn: { width: 24, alignItems: 'center' },
  etoile: { fontSize: 18, fontWeight: '700' },
  chip: { width: 28, height: 28, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  chipTxt: { fontSize: 14 },
  nom: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  nomMasque: { color: UI.muted },
  sub: { fontSize: 11.5, color: UI.faint, marginTop: 1 },
  oeilBtn: { width: 30, alignItems: 'center' },
  oeil: { fontSize: 17 },
  pied: { fontSize: 11, color: UI.faint, padding: 14, lineHeight: 16, borderTopWidth: 0.5, borderTopColor: UI.border },
});
